import { useCallback, useRef, useState, type PointerEvent } from 'react'

interface UsePanToCloseOptions {
  onClose: () => void
  threshold?: number
}

export function usePanToClose({ onClose, threshold = 96 }: UsePanToCloseOptions) {
  const [dragY, setDragY] = useState(0)
  const dragRef = useRef({ active: false, pointerId: -1, startY: 0, y: 0 })

  const onPointerDown = useCallback((event: PointerEvent<HTMLElement>) => {
    if (event.pointerType === 'mouse' && event.button !== 0) return
    const target = event.target as HTMLElement
    if (target.closest('button,a,input,textarea,select,[data-no-pan-close]')) return

    dragRef.current = {
      active: true,
      pointerId: event.pointerId,
      startY: event.clientY,
      y: 0,
    }
    event.currentTarget.setPointerCapture(event.pointerId)
  }, [])

  const onPointerMove = useCallback((event: PointerEvent<HTMLElement>) => {
    if (!dragRef.current.active || dragRef.current.pointerId !== event.pointerId) return
    const nextY = Math.max(0, event.clientY - dragRef.current.startY)
    dragRef.current.y = nextY
    setDragY(nextY)
    if (nextY > 0) event.preventDefault()
  }, [])

  const finish = useCallback((event: PointerEvent<HTMLElement>) => {
    if (!dragRef.current.active || dragRef.current.pointerId !== event.pointerId) return
    const shouldClose = dragRef.current.y >= threshold
    dragRef.current.active = false
    dragRef.current.pointerId = -1
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId)
    }
    setDragY(0)
    if (shouldClose) onClose()
  }, [onClose, threshold])

  return {
    dragY,
    dragHandleProps: {
      onPointerDown,
      onPointerMove,
      onPointerUp: finish,
      onPointerCancel: finish,
    },
  }
}
